import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Checkbox, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { t } from "i18next";
import type { DateTime } from "luxon";

import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useJobs } from "../../hooks/useJobs";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { CONTROL_HEIGHT } from "../../theme/tokens";
import { NurseFullRoleShort, type NurseRole } from "../../utils/models";
import { MAX_OPEN_SHIFT_DURATION_HOURS } from "../../utils/openShiftDuration";
import { nurseRoleDisplayName } from "../../utils/utilFunctions";
import { DIFF_HOURS_WARNING } from "../../utils/warningDialogs/utils";
import { EditTimePopover } from "../EditTimePopover";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";

interface CreateShiftRowProps {
	/** Let fields wrap inside a bounded form instead of the legacy fixed-width row. */
	responsive?: boolean;
	roleGroups: string[];
	setRoleGroups: (roleGroups: string[]) => void;
	jobId?: string;
	setJobId?: (jobId: string) => void;
	amount: number;
	setAmount: (amount: number | undefined) => void;
	times: { startTime: DateTime | null; endTime: DateTime | null };
	/** Optional fallback times to use when times.startTime/endTime are null. */
	fallbackTimes?: { startTime: DateTime; endTime: DateTime };
	updateTimes: (startTime: DateTime, endTime: DateTime) => void;
	isChecked?: boolean;
	setIsChecked?: (isChecked: boolean) => void;
	isValid?: boolean;
	location?: string;
	setLocation?: (location: string) => void;
	availableLocations?: string[];
	/** Sub-role options for this row (already filtered by the selected agency source, and by jobId or — in role-group mode — the row's role groups). Only rendered when non-empty. */
	subRoleOptions?: NurseRole[];
	subRole?: NurseRole;
	setSubRole?: (subRole: NurseRole | undefined) => void;
	/** Annie walkthrough anchor. Declared explicitly — a closed props type drops a `data-*` prop silently. */
	"data-annie-target"?: string;
}

/** Mirror the old per-MenuItem `textTransform: capitalize` now that item-level sx no longer applies. */
const capitalizeWords = (text: string): string => text.replace(/\b\w/g, (char) => char.toUpperCase());

const RequiredFieldOutline = ({ children, className }: { children: ReactNode; className?: string }) => {
	return (
		<Box
			className={className}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
			}}
		>
			{children}
			<Typography
				sx={{
					position: "absolute",
					top: 0,
					right: 0,
					fontSize: "16px",
					color: "#EF4444",
					fontWeight: "bold",
					transform: "translate(50%, -50%)",
					zIndex: 1,
					pointerEvents: "none",
					userSelect: "none",
				}}
			>
				*
			</Typography>
		</Box>
	);
};

export const CreateShiftRow: React.FC<CreateShiftRowProps> = (props) => {
	const { chosenDepartment } = useChosenDepartment();
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const availableJobs = useJobs();
	const [timeEditingAnchor, setTimeEditingAnchor] = useState<null | HTMLElement>(null);

	const editTimePickerClicked = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setTimeEditingAnchor(event.currentTarget);
	}, []);
	const closeTimePicker = useCallback(() => {
		setTimeEditingAnchor(null);
	}, []);

	const longShiftLimit = chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING;

	// Check if shift duration is long enough to show warning
	const showWarning = useMemo(() => {
		if (props.times.startTime && props.times.endTime) {
			const hours = props.times.endTime.diff(props.times.startTime, "hours").hours;
			return hours >= longShiftLimit;
		}
		return false;
	}, [props.times.startTime, props.times.endTime, longShiftLimit]);

	const effectiveStartTime = props.times.startTime ?? props.fallbackTimes?.startTime;
	const effectiveEndTime = props.times.endTime ?? props.fallbackTimes?.endTime ?? effectiveStartTime ?? null;
	const timeSummary = useMemo(() => {
		if (!props.times.startTime || !props.times.endTime) return null;
		const durationMinutes = props.times.endTime.toUTC().diff(props.times.startTime.toUTC(), "minutes").minutes;
		const hours = Math.floor(durationMinutes / 60);
		const minutes = durationMinutes % 60;
		const duration = minutes === 0 ? t("post.shift.time.duration.hours", { hours }) : t("post.shift.time.duration.hours.minutes", { hours, minutes });
		return {
			duration,
			isNextDay: !props.times.endTime.hasSame(props.times.startTime, "day"),
		};
	}, [props.times.startTime, props.times.endTime, t]);

	return (
		<Box
			data-testid="post-shift-row"
			data-annie-target={props["data-annie-target"]}
			sx={{
				display: "flex",
				gap: 2,
				alignItems: "center",
				...(props.responsive && {
					flex: 1,
					minWidth: 0,
					flexWrap: "wrap",
					"& > .MuiBox-root": { minWidth: 0 },
					"& > .post-shift-job-field": { flex: { xs: "1 1 70%", md: "2 1 25%" } },
					"& > .post-shift-count-field": { flex: { xs: "1 1 100%", sm: "1 1 40%", md: "1 1 15%" } },
					"& > .post-shift-time-field": { flex: { xs: "1 1 100%", sm: "1 1 40%", md: "1 1 25%" } },
				}),
			}}
		>
			{props.setIsChecked ? <Checkbox checked={props.isChecked} onChange={(e) => props.setIsChecked?.(e.target.checked)} /> : <span />}
			<RequiredFieldOutline className="post-shift-job-field">
				{props.setJobId ? (
					<SystemDropdown
						label={t("job")}
						items={availableJobs.map((job) => ({ value: job.id, label: job.displayName }))}
						selectedItem={props.jobId}
						onChange={(value) => {
							if (value === undefined) return;
							props.setJobId?.(value);
							// CLEANUP: remove this roleGroups derivation when role groups are removed (INH-3006)
							if (!jobManagementEnabled && value) {
								// Find role groups that contain roles of nurses with this job
								const matchingGroups = new Set<string>();
								// DEFAULT: no role groups to match until the roster arrives (the provider has loaded it before a panel opens).
								for (const nurse of roster ?? []) {
									if (nurse.jobs?.some((j) => j.id === value)) {
										for (const [groupName, groupRoles] of Object.entries(chosenDepartment.role_groups)) {
											if (groupRoles.some((r) => r === NurseFullRoleShort[nurse.role as keyof typeof NurseFullRoleShort])) {
												matchingGroups.add(groupName);
											}
										}
									}
								}
								props.setRoleGroups(matchingGroups.size > 0 ? Array.from(matchingGroups) : []);
							} else {
								props.setRoleGroups([]);
							}
						}}
						data-testid="post-shift-role-select"
						formControlSx={{ width: props.responsive ? "100%" : "400px", minWidth: 0 }}
					/>
				) : (
					<SystemDropdown
						multiple
						// Keep search a pure filter: the old MUI Select never auto-picked a role group, so a unique
						// query match must not commit a selection before the user explicitly chooses one.
						autoSelectSingleMatch={false}
						label={t("choose.roles")}
						items={Object.entries(chosenDepartment.role_groups).map(([name, roles]) => ({
							value: name,
							label: capitalizeWords(`${name} (${roles.map((r) => nurseRoleDisplayName[r as NurseRole] ?? r).join(", ")})`),
						}))}
						selectedItems={props.roleGroups}
						onChangeMultiple={(values) => props.setRoleGroups(values)}
						renderValue={(selected) => {
							const values = Array.isArray(selected) ? selected : selected ? [selected] : [];
							if (values.length === 0) {
								return <Box sx={{ color: "primary.dark" }}>{t("select.roles")}</Box>;
							}
							return values.join(", ");
						}}
						data-testid="post-shift-role-select"
						formControlSx={{ width: props.responsive ? "100%" : "400px", minWidth: 0 }}
					/>
				)}
			</RequiredFieldOutline>
			<RequiredFieldOutline className="post-shift-count-field">
				<SystemValidationTextField
					dataTestId="post-shift-count"
					label={t("amount.of.slots")}
					inputType="number"
					value={props.amount.toString()}
					onChange={(e) => {
						const value = Number(e.target.value);
						props.setAmount(value >= 0 ? value : undefined);
					}}
					variant="outlined"
					reserveHeightForHelperText={false}
					formControlSx={props.responsive ? { width: "100%", minWidth: 0 } : undefined}
					inputProps={{
						slotProps: {
							input: {
								min: 0,
							},
						},
					}}
				/>
			</RequiredFieldOutline>
			<RequiredFieldOutline className="post-shift-time-field">
				<Box
					data-testid="post-shift-time-picker"
					sx={{
						width: props.responsive ? "100%" : "200px",
						display: "flex",
						alignItems: "center",
						border: showWarning ? "1px solid #F59E0B" : "1px solid #4D557933",
						borderRadius: "8px",
						px: 1,
						py: 0.25,
						height: CONTROL_HEIGHT.LARGE,
						justifyContent: "space-between",
						cursor: "pointer",
						gap: 1,
						bgcolor: showWarning ? "#FEF3C7" : "transparent",
					}}
					onClick={editTimePickerClicked}
				>
					<Typography
						sx={{
							fontSize: "14px",
							color: "primary.dark",
						}}
					>
						<Box component="span" sx={{ display: "block" }}>
							{props.times.startTime ? props.times.startTime.toFormat("h:mm a") : "--:--"} -{" "}
							{props.times.endTime ? props.times.endTime.toFormat("h:mm a") : "--:--"}
						</Box>
						{timeSummary && (
							<Box component="span" sx={{ display: "block", color: "text.secondary", typography: "caption" }}>
								{timeSummary.isNextDay ? `${t("post.shift.time.next.day")} · ` : ""}
								{timeSummary.duration}
							</Box>
						)}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						{showWarning && (
							<Tooltip title={t("shift.duration.warning", { hours: longShiftLimit })}>
								<WarningAmberIcon sx={{ fontSize: "16px", color: "#F59E0B" }} />
							</Tooltip>
						)}
						<AccessTimeIcon sx={{ fontSize: "16px", color: "primary.dark" }} />
					</Box>
				</Box>
			</RequiredFieldOutline>
			{timeEditingAnchor && (
				<EditTimePopover
					startTime={effectiveStartTime ?? undefined}
					onTimeChange={(values) => props.updateTimes(values.newStartTime, values.newEndTime)}
					endTime={effectiveEndTime ?? undefined}
					onClose={closeTimePicker}
					anchorEl={timeEditingAnchor}
					jobId={props.jobId}
					roleGroups={props.roleGroups ?? []}
					maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
				/>
			)}
			{props.availableLocations && props.setLocation && (
				<SystemDropdown
					label={t("pickup.by.location.select.location")}
					items={props.availableLocations}
					selectedItem={props.location ?? ""}
					onChange={(value) => props.setLocation?.(value ?? "")}
					clearable
					data-testid="post-shift-location"
					formControlSx={{ width: { xs: 160, xl: 200 } }}
				/>
			)}
			{props.subRoleOptions && props.subRoleOptions.length > 0 && props.setSubRole && (
				<RequiredFieldOutline>
					<SystemDropdown
						label={t("sub.role")}
						items={props.subRoleOptions.map((r) => ({ value: r, label: nurseRoleDisplayName[r] ?? r }))}
						selectedItem={props.subRole}
						onChange={(value) => props.setSubRole?.(value)}
						formControlSx={{ width: { xs: 120, xl: 180 } }}
					/>
				</RequiredFieldOutline>
			)}
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
				{props.isChecked && !props.isValid && (
					<Tooltip title={t("incomplete.row.warning")}>
						<WarningAmberIcon sx={{ fontSize: "20px", color: "#EF4444" }} />
					</Tooltip>
				)}
			</Box>
		</Box>
	);
};
